import './NoImagePlaceholder.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useParams } from 'react-router';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { S_Link } from '../../../../design/links/S_Link';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { Home } from '../../../routes/config/Home';
import { IWorkspaceService } from '../../workspaces/workspaces.service';
import { WorkspaceRole } from '../../workspaces/workspaces.store';
import { NoImageReason } from '../annotations.interface';

library.add(faCheckCircle, faCircle);

interface INoImagePlaceholderProps {
  onRefresh: () => Promise<void>;
  workspaceId: string;
  noImageReason: NoImageReason;
  canReview: boolean;
  mode: 'Review' | 'Annotate';
}

export const NoImagePlaceholder = observer((props: INoImagePlaceholderProps): ReactElement => {
  const { projectId } = useParams<{ projectId: string }>();
  const workspaceService = useInjection(IWorkspaceService.$);
  const role = workspaceService.getUserRoleInCurrentWorkspace();

  const { t } = useTranslator();

  const getTranslationKey = (): TranslationKey => {
    switch (props.noImageReason) {
      case NoImageReason.Credits:
        return 'no_more_credits_available';
      case NoImageReason.NoMoreImages:
        return props.mode === 'Annotate' ? 'no_more_images_to_annotate' : 'no_more_images_to_review';
      case NoImageReason.Locked:
        return props.mode === 'Annotate' ? 'not_annotated_images_are_locked' : 'not_reviewed_images_are_locked';
      case NoImageReason.Unknown:
      case NoImageReason.QueueError:
      default:
        return 'no_more_images_in_queue';
    }
  };

  return (
    <div className='no-image-ph'>
      <S_Card className='center'>
        <ColumnLayout size='md'>
          <h5>{t(getTranslationKey())}</h5>
          <RowLayout
            size='l'
            className='placeholder-buttons'
          >
            <Button
              onClick={() => {
                void props.onRefresh();
              }}
            >
              {t('refresh_view')}
            </Button>
            {props.mode === 'Review' && (
              <S_Link
                color='text'
                to={Home.Annotations.Create.withParams({ workspaceId: props.workspaceId, projectId })}
              >
                <Button>{t('annotate_more_images')}</Button>
              </S_Link>
            )}
            {props.mode === 'Annotate' && props.canReview && (
              <S_Link
                color='text'
                to={Home.Annotations.Review.withParams({ workspaceId: props.workspaceId, projectId })}
              >
                <Button>{t('start_reviewing_images')}</Button>
              </S_Link>
            )}
            {role === WorkspaceRole.Worker && (
              <S_Link
                color='text'
                to={Home.Projects.List.All.withParams({ workspaceId: props.workspaceId })}
              >
                <Button>{t('ok_move_to_project_list')}</Button>
              </S_Link>
            )}
            {role !== WorkspaceRole.Worker && (
              <S_Link
                color='text'
                to={Home.Projects.Details.Overview.withParams({ workspaceId: props.workspaceId, projectId })}
              >
                <Button>{t('ok_move_to_dashboard')}</Button>
              </S_Link>
            )}
          </RowLayout>
        </ColumnLayout>
      </S_Card>
    </div>
  );
});
